import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useRxCollectionEpocusEncounter } from "../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusEncounterPatient,
  RELATIONSHIP_TO_HOLDER_OPTIONS,
  type RelationshipToHolder,
  useRxCollectionEpocusEncounterPatient,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-encounter-patient";
import { splitDicomPatientName } from "../../../_lib/dicom-patient-name";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";

const RELATIONSHIP_LABELS: Record<RelationshipToHolder, string> = {
  SELF: "Self",
  SPOUSE: "Spouse",
  CHILD: "Child",
  OTHER: "Other",
};

const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "O", label: "Other" },
] as const;

const GENDER_LABELS: Record<string, string> = {
  M: "Male",
  F: "Female",
  O: "Other",
};

const parseDicomPatientName = (
  raw: string | null | undefined,
): { firstName: string; lastName: string } => {
  const name = raw?.trim();
  if (!name) return { firstName: "", lastName: "" };
  const dicomParts = splitDicomPatientName(name);
  if (dicomParts) {
    return { firstName: dicomParts.given, lastName: dicomParts.family };
  }
  const parts = name.split(/\s+/);
  if (parts.length >= 2) {
    return {
      firstName: parts.slice(0, -1).join(" "),
      lastName: parts[parts.length - 1],
    };
  }
  return { firstName: "", lastName: name };
};

// DICOM PatientSex is "M" | "F" | "O"; normalise anything else to "" (no preset).
const normaliseDicomGender = (raw: string | null | undefined): string => {
  const code = raw?.trim().toUpperCase();
  return code && code in GENDER_LABELS ? code : "";
};

const SectionCard = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  transition: "opacity 0.2s ease, background-color 0.2s ease",
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const SectionLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: EPOCUS_TEXT_SECONDARY,
});

const FieldRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
});

const FieldLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 600,
  color: EPOCUS_TEXT_SECONDARY,
  marginBottom: 4,
});

const ReadOnlyValue = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 500,
  color: EPOCUS_TEXT_PRIMARY,
});

type FormState = {
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  insuranceProviderName: string;
  policyNumber: string;
  groupNumber: string;
  policyholderName: string;
  relationshipToHolder: RelationshipToHolder | "";
};

const EMPTY_FORM: FormState = {
  firstName: "",
  lastName: "",
  dob: "",
  gender: "",
  insuranceProviderName: "",
  policyNumber: "",
  groupNumber: "",
  policyholderName: "",
  relationshipToHolder: "",
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const validate = (form: FormState): FieldErrors => {
  const errors: FieldErrors = {};
  if (!form.firstName.trim()) errors.firstName = "Required";
  if (!form.lastName.trim()) errors.lastName = "Required";
  if (!form.insuranceProviderName.trim())
    errors.insuranceProviderName = "Required";
  if (!form.policyNumber.trim()) errors.policyNumber = "Required";
  if (!form.relationshipToHolder) errors.relationshipToHolder = "Required";
  return errors;
};

export const PatientDetailsForm: FC<{
  encounterId: string;
  savedPatient: EpocusEncounterPatient | null;
  hideHeader?: boolean;
}> = ({ encounterId, savedPatient, hideHeader }) => {
  const collection = useRxCollectionEpocusEncounterPatient();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isSaved = savedPatient !== null;

  // Prefill first/last name, DOB and gender from the DICOM demographics
  // synced onto the encounter, so the operator confirms rather than re-keys data
  // the ultrasound machine already captured. Best-effort and one-shot: only runs
  // for an unsaved patient and never overwrites a field the operator has touched.
  useEffect(() => {
    if (isSaved || !encounterCollection) return;
    let cancelled = false;
    (async () => {
      try {
        const encounterDoc = await encounterCollection
          .findOne(encounterId)
          .exec();
        if (cancelled || !encounterDoc) return;
        const { firstName, lastName } = parseDicomPatientName(
          encounterDoc.patientName,
        );
        const dob = encounterDoc.patientDob?.slice(0, 10) ?? "";
        const gender = normaliseDicomGender(encounterDoc.patientGender);
        if (!firstName && !lastName && !dob && !gender) return;
        setForm((prev) => ({
          ...prev,
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
          dob: prev.dob || dob,
          gender: prev.gender || gender,
        }));
      } catch (err) {
        console.error("Failed to prefill patient details from DICOM", {
          encounterId,
          error: err,
        });
        Sentry.captureException(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [encounterCollection, encounterId, isSaved]);

  const set = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    if (!collection) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      await collection.upsert({
        encounterId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        dob: form.dob || null,
        gender: form.gender || null,
        insuranceProviderName: form.insuranceProviderName.trim(),
        policyNumber: form.policyNumber.trim(),
        groupNumber: form.groupNumber.trim() || null,
        policyholderName: form.policyholderName.trim() || null,
        relationshipToHolder: form.relationshipToHolder as RelationshipToHolder,
      });
    } catch (err) {
      console.error("Failed to save patient details", {
        encounterId,
        error: err,
      });
      Sentry.captureException(err);
      setSaveError("Failed to save patient details. Please try again.");
      setIsSaving(false);
      return;
    }

    // Propagate the patient name to the encounter so the header reflects it
    // instead of showing "Unknown Patient" for unmatched encounters.
    // Best-effort: a failure here must not surface as a billing-save error.
    try {
      const encounterDoc = await encounterCollection
        ?.findOne(encounterId)
        ?.exec();
      if (encounterDoc != null && encounterDoc.patientName == null) {
        await encounterDoc.incrementalPatch({
          patientName: `${form.firstName.trim()} ${form.lastName.trim()}`,
        });
      }
    } catch (err) {
      console.error("Failed to propagate patient name to encounter", {
        encounterId,
        error: err,
      });
      Sentry.captureException(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isSaved) {
    return (
      <SectionCard style={{ backgroundColor: "#f9fafb", opacity: 0.85 }}>
        {!hideHeader && (
          <SectionHeader>
            <SectionLabel>Patient billing details</SectionLabel>
            <Typography
              style={{
                fontSize: rem(12),
                color: "#16a34a",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              ✓ Saved
            </Typography>
          </SectionHeader>
        )}

        <FieldRow>
          <Box>
            <FieldLabel>First name</FieldLabel>
            <ReadOnlyValue>{savedPatient.firstName}</ReadOnlyValue>
          </Box>
          <Box>
            <FieldLabel>Last name</FieldLabel>
            <ReadOnlyValue>{savedPatient.lastName}</ReadOnlyValue>
          </Box>
        </FieldRow>

        <FieldRow>
          {savedPatient.dob && (
            <Box>
              <FieldLabel>Date of birth</FieldLabel>
              <ReadOnlyValue>{savedPatient.dob}</ReadOnlyValue>
            </Box>
          )}
          {savedPatient.gender && (
            <Box>
              <FieldLabel>Gender</FieldLabel>
              <ReadOnlyValue>
                {GENDER_LABELS[savedPatient.gender] ?? savedPatient.gender}
              </ReadOnlyValue>
            </Box>
          )}
        </FieldRow>

        <Box
          style={{
            height: 1,
            backgroundColor: EPOCUS_BORDER,
          }}
        />

        <FieldRow>
          <Box style={{ gridColumn: "1 / -1" }}>
            <FieldLabel>Insurance provider</FieldLabel>
            <ReadOnlyValue>{savedPatient.insuranceProviderName}</ReadOnlyValue>
          </Box>
        </FieldRow>
        <FieldRow>
          <Box>
            <FieldLabel>Policy / member ID</FieldLabel>
            <ReadOnlyValue>{savedPatient.policyNumber}</ReadOnlyValue>
          </Box>
          {savedPatient.groupNumber && (
            <Box>
              <FieldLabel>Group number</FieldLabel>
              <ReadOnlyValue>{savedPatient.groupNumber}</ReadOnlyValue>
            </Box>
          )}
        </FieldRow>
        <FieldRow>
          <Box>
            <FieldLabel>Relationship to holder</FieldLabel>
            <ReadOnlyValue>
              {RELATIONSHIP_LABELS[savedPatient.relationshipToHolder]}
            </ReadOnlyValue>
          </Box>
          {savedPatient.policyholderName && (
            <Box>
              <FieldLabel>Policyholder name</FieldLabel>
              <ReadOnlyValue>{savedPatient.policyholderName}</ReadOnlyValue>
            </Box>
          )}
        </FieldRow>
      </SectionCard>
    );
  }

  return (
    <SectionCard style={{ backgroundColor: EPOCUS_WHITE }}>
      {!hideHeader && (
        <SectionHeader>
          <SectionLabel>Patient billing details</SectionLabel>
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            Not yet saved
          </Typography>
        </SectionHeader>
      )}

      <FieldRow>
        <TextField
          label="First name"
          value={form.firstName}
          onChange={(e) => set("firstName", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          required
          error={!!errors.firstName}
          helperText={errors.firstName}
          inputProps={{ "aria-label": "First name" }}
        />
        <TextField
          label="Last name"
          value={form.lastName}
          onChange={(e) => set("lastName", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          required
          error={!!errors.lastName}
          helperText={errors.lastName}
          inputProps={{ "aria-label": "Last name" }}
        />
      </FieldRow>

      <FieldRow>
        <TextField
          label="Date of birth (optional)"
          type="date"
          value={form.dob}
          onChange={(e) => set("dob", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ "aria-label": "Date of birth" }}
        />
        <TextField
          select
          label="Gender (optional)"
          value={form.gender}
          onChange={(e) => set("gender", e.target.value as string)}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Gender" }}
        >
          {GENDER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </FieldRow>

      <Box
        style={{
          height: 1,
          backgroundColor: EPOCUS_BORDER,
          marginTop: 4,
          marginBottom: 4,
        }}
      />

      <TextField
        label="Insurance provider / payer name"
        value={form.insuranceProviderName}
        onChange={(e) => set("insuranceProviderName", e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        required
        error={!!errors.insuranceProviderName}
        helperText={errors.insuranceProviderName}
        inputProps={{ "aria-label": "Insurance provider" }}
      />

      <FieldRow>
        <TextField
          label="Policy / member ID"
          value={form.policyNumber}
          onChange={(e) => set("policyNumber", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          required
          error={!!errors.policyNumber}
          helperText={errors.policyNumber}
          inputProps={{ "aria-label": "Policy number" }}
        />
        <TextField
          label="Group number (optional)"
          value={form.groupNumber}
          onChange={(e) => set("groupNumber", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Group number" }}
        />
      </FieldRow>

      <FieldRow style={{ marginTop: 4 }}>
        <TextField
          select
          label="Relationship to holder"
          value={form.relationshipToHolder}
          onChange={(e) =>
            set("relationshipToHolder", e.target.value as string)
          }
          variant="outlined"
          size="small"
          fullWidth
          required
          error={!!errors.relationshipToHolder}
          helperText={errors.relationshipToHolder}
          inputProps={{ "aria-label": "Relationship to holder" }}
        >
          {RELATIONSHIP_TO_HOLDER_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {RELATIONSHIP_LABELS[opt]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Policyholder name (optional)"
          value={form.policyholderName}
          onChange={(e) => set("policyholderName", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Policyholder name" }}
        />
      </FieldRow>

      {saveError && (
        <Typography
          style={{ fontSize: rem(13), color: "#dc2626", fontWeight: 600 }}
        >
          {saveError}
        </Typography>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isSaving}
        aria-label={
          isSaving ? "Saving patient details…" : "Save patient details"
        }
        style={{
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 700,
          fontSize: rem(14),
          alignSelf: "center",
          minHeight: 40,
          minWidth: 120,
        }}
        disableElevation
      >
        {isSaving ? <CircularProgress size={18} color="inherit" /> : "Save"}
      </Button>
    </SectionCard>
  );
};
